import React from 'react';
import PropTypes from 'prop-types';

import { useIntl } from '@edx/frontend-platform/i18n';
import { Badge, Card, Truncate } from '@openedx/paragon';

import { learningMfeUrl } from 'data/services/lms/urls';
import AccessTypeBadge from 'components/Badges/AccessTypeBadge';

import messages from './messages';
import './index.scss';

export const DEFAULT_BANNER_IMAGE_URL = 'https://alnafi-main-backend.s3.us-east-1.amazonaws.com/course_images/demo.png';

/**
 * Course card. Renders backend-provided title/banner_image_url/course_home_url
 * when present, and falls back gracefully (courseId as title, a default banner
 * image, a locally-built course URL) for any course the backend hasn't
 * enriched yet.
 */
export const CourseCardMinimal = ({
  courseId, isActive, accessType, title, bannerImageUrl, courseHomeUrl,
}) => {
  const { formatMessage } = useIntl();
  const displayTitle = title || courseId;
  const href = courseHomeUrl || learningMfeUrl(`/course/${courseId}/home`);

  return (
    <Card
      className={`course-card-minimal ${isActive ? '' : 'course-card-minimal-inactive'}`}
      as="a"
      href={href}
      aria-label={formatMessage(messages.viewCourse, { title: displayTitle })}
      isClickable
    >
      <Card.ImageCap
        src={bannerImageUrl || DEFAULT_BANNER_IMAGE_URL}
        srcAlt={formatMessage(messages.bannerAlt, { title: displayTitle })}
      />
      <Card.Header
        title={(
          <Truncate lines={2} className="course-card-minimal-title">
            {displayTitle}
          </Truncate>
        )}
      />
      <Card.Section>
        <div className="d-flex flex-wrap align-items-center course-card-minimal-badges">
          <AccessTypeBadge accessType={accessType} />
          {!isActive && (
            <Badge variant="light" className="course-card-minimal-inactive-badge">
              {formatMessage(messages.inactive)}
            </Badge>
          )}
        </div>
      </Card.Section>
    </Card>
  );
};

CourseCardMinimal.propTypes = {
  courseId: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  accessType: PropTypes.string,
  title: PropTypes.string,
  bannerImageUrl: PropTypes.string,
  courseHomeUrl: PropTypes.string,
};

CourseCardMinimal.defaultProps = {
  isActive: true,
  accessType: null,
  title: null,
  bannerImageUrl: null,
  courseHomeUrl: null,
};

export default CourseCardMinimal;
